import { useParams } from "react-router-dom";
import { useExamDetail, useExamQuestionStatusGet } from "../../react-query/queries";
import { useAuth } from "../../utils/Auth/AuthProvider";
import { useState, useEffect } from "react";
import ExamQuestionsInitial from "./ExamQuestionsInitial";
import AddQuestionsManual from './AddQuestionsManual'
import ExtractQuestionsFromFile from './ExtractQuestionsFromFile'
import ExtractQuestionsFromJSON from './ExtractQuestionsFromJSON'
import { Dispatch, SetStateAction } from 'react'
import { Upload } from 'lucide-react'
import { Spinner, Badge, Button, Switch } from '../../school_panel/components/ui'

// Design `.trow`: a bordered row with a label, a hint and a switch.
const Toggle = ({ checked, label, hint, onChange }: { checked: boolean; label: string; hint: string; onChange: (v: boolean) => void }) => (
    <div className="flex min-w-0 items-center justify-between gap-4 rounded-[14px] border border-line bg-surface-2 px-4 py-3.5">
        <span className="min-w-0">
            <span id={`tg-${label.replace(/\s+/g, '-').toLowerCase()}`} className="block font-semibold text-ink">{label}</span>
            <span className="block text-xs text-muted">{hint}</span>
        </span>
        <Switch tone="navy" checked={checked} onChange={onChange} label={label} labelledBy={`tg-${label.replace(/\s+/g, '-').toLowerCase()}`} />
    </div>
)

type View = 'editor' | 'extract' | 'json' | 'manual'

// Design `.opt` / `.opt.on` import pills.
const importPill = (on: boolean) =>
    `inline-flex min-h-[36px] items-center gap-1.5 rounded-full border px-[13px] text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${on ? 'border-navy bg-navy text-white' : 'border-line-strong bg-surface text-ink hover:border-slate'}`

const ExamQuestionAddCard = ({
    examId: propExamId,
    onProceedToMeta,
    onRegisterExtractFn,
    onRegisterProceedFn,
    onQuestionsExtracted,
    onQuestionsChange,
    initialQuestions,
    isExtracting,
    setIsExtracting,
}: {
    examId?: string | undefined
    onProceedToMeta?: (qs: any[]) => void
    onRegisterExtractFn?: (fn: () => void) => void
    onRegisterProceedFn?: (fn: () => void) => void
    onQuestionsExtracted?: (qs?: any[]) => void
    /** Called with the current question list whenever it changes. */
    onQuestionsChange?: (qs: any[]) => void
    /** Questions already added (kept by the wizard when the user steps back and returns). */
    initialQuestions?: any[]
    isExtracting: boolean
    setIsExtracting: Dispatch<SetStateAction<boolean>>
}) => {
    const { token } = useAuth();
    const params = useParams();
    const routeExamId = params.examId
    const examId = propExamId ?? routeExamId
    const [view, setView] = useState<View>('editor');
    const [questionsState, setQuestionsState] = useState<any[]>(initialQuestions ?? []);
    const [proposeAnswer, setProposeAnswer] = useState<boolean>(false);
    const [proposeExplanation, setProposeExplanation] = useState<boolean>(false);

    const { status: examStatus, data: examData, error: examError } = useExamDetail(token, examId)
    const { data: qStatData } = useExamQuestionStatusGet(token, examId)

    // Register proceed function so parent can trigger "proceed to processing" from footer
    useEffect(() => {
        if (onRegisterProceedFn && questionsState.length > 0) {
            onRegisterProceedFn(() => {
                if (onProceedToMeta) onProceedToMeta(questionsState)
            })
        }
    }, [questionsState, onRegisterProceedFn, onProceedToMeta])

    useEffect(() => { onQuestionsChange?.(questionsState) }, [questionsState, onQuestionsChange])

    const exam = (examData as any)?.exam
    const gradeText = String(exam?.school_grade_name || exam?.grade || '').replace(/^\s*grade\s+/i, '')
    const timeVal = exam?.time ?? exam?.time_given
    const statusVal = (qStatData as any)?.stat || exam?.status || 'Draft'
    const info: Array<[string, string]> = [
        ['Exam name', exam?.name || ''],
        ['Subject', exam?.subject || ''],
        ['Grade', gradeText ? `Grade ${gradeText}` : ''],
        ['Time given', timeVal ? `${timeVal} minutes` : 'Not set'],
    ]

    const hasQuestions = questionsState.length > 0
    // With nothing added there is no editor to show, so the file import is the default panel.
    const shown: View = view === 'editor' && !hasQuestions ? 'extract' : view
    const onExtract = (paras: any[]) => {
        setQuestionsState(paras);
        setView('editor');
        if (onQuestionsExtracted) onQuestionsExtracted(paras);
        if (setIsExtracting) setIsExtracting(false);
    }

    const importHeader = (title: string) => (
        <div className="mb-[18px] flex flex-wrap items-center justify-between gap-3">
            <div>
                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Import</p>
                <h2 id="qe-title" className="mt-1 font-display text-[17px] font-bold tracking-[-0.01em] text-ink">{title}</h2>
            </div>
            {hasQuestions && <Button variant="ghost" onClick={() => setView('editor')}>Back to editor</Button>}
        </div>
    )

    const panel = shown === 'editor' ? undefined : shown === 'extract' ? (
        <>
            {importHeader('Extract from file')}
            <div className="mb-[18px] flex flex-col gap-2.5">
                <Toggle checked={proposeAnswer} label="Propose answer" hint="AI suggests the correct choice when the source has no key" onChange={setProposeAnswer} />
                <Toggle checked={proposeExplanation} label="Propose explanation" hint="Drafts an explanation for each question" onChange={setProposeExplanation} />
            </div>
            <ExtractQuestionsFromFile
                onExtract={onExtract}
                onRegisterExtractFn={onRegisterExtractFn}
                isExtracting={isExtracting}
                setIsExtracting={setIsExtracting}
                proposeAnswer={proposeAnswer}
                proposeExplanation={proposeExplanation}
                jobScope={examId}
            />
        </>
    ) : shown === 'json' ? (
        <>
            {importHeader('Extract from JSON')}
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            <ExtractQuestionsFromJSON onExtract={(paras: any[]) => onExtract(paras)} />
        </>
    ) : (
        <>
            {importHeader('Add a question')}
            <AddQuestionsManual onAdd={(q) => setQuestionsState((prev) => [...prev, q])} />
        </>
    )

    const footer = (
        <>
            <p className="mb-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Import questions</p>
            <div className="flex flex-wrap gap-2">
                <button type="button" aria-pressed={shown === 'extract'} onClick={() => setView('extract')} className={importPill(shown === 'extract')}>
                    <Upload size={16} strokeWidth={1.8} aria-hidden />From file
                </button>
                <button type="button" aria-pressed={shown === 'json'} onClick={() => setView('json')} className={importPill(shown === 'json')}>
                    <span aria-hidden>{'{ }'}</span> From JSON
                </button>
            </div>
        </>
    )

    return (
        <div className="min-w-0">
            {examError != null ? (
                <p role="alert" className="mb-[18px] rounded-[12px] border border-danger/30 bg-danger-bg px-3.5 py-2.5 text-sm text-danger-ink">The exam details could not be loaded. Refresh the page and try again.</p>
            ) : examStatus === 'success' ? (
                <dl className="mb-[18px] grid grid-cols-2 gap-1 rounded-[16px] border border-line bg-surface p-1.5 sm:grid-cols-3 xl:grid-cols-5">
                    {info.map(([k, v]) => (
                        <div key={k} className="min-w-0 rounded-[12px] px-3.5 py-2.5">
                            <dt className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted">{k}</dt>
                            <dd className={`mt-1 break-words font-semibold text-ink ${k === 'Time given' ? 'font-mono' : ''}`}>{v || '—'}</dd>
                        </div>
                    ))}
                    <div className="min-w-0 rounded-[12px] px-3.5 py-2.5">
                        <dt className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted">Status</dt>
                        <dd className="mt-1"><Badge tone={statusVal === 'Active' ? 'success' : 'info'}>{statusVal}</Badge></dd>
                    </div>
                </dl>
            ) : (
                <div className="mb-[18px] flex items-center gap-2 rounded-[16px] border border-line bg-surface px-4 py-3 text-sm text-muted"><Spinner size="sm" /> Loading exam details…</div>
            )}

            <ExamQuestionsInitial
                questions={questionsState}
                onProceedToMeta={onProceedToMeta}
                onQuestionsChange={setQuestionsState}
                onEdit={() => onQuestionsChange?.([...questionsState])}
                onAdd={() => setView('manual')}
                panel={panel}
                footer={footer}
            />
        </div>
    )
}

export default ExamQuestionAddCard
